import React, { useCallback, useLayoutEffect, useState, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import ReactTooltip from 'react-tooltip';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import { Icon, Div, Image, Text } from '@pinpoint/design-kit';

import { Switch, ModelsDropdown } from 'src/components';
import useModels from 'src/hooks/swr/useModels';
import usePlatformConfig from 'src/hooks/usePlatformConfig';
import { appActions, segmentsListActions } from 'src/store/actions';
import CONSTANTS from 'src/store/constants';
import { checkHiddenByRole } from 'src/functions';

import BaseDialog from './base';

const defaultValues = {
  model: '',
};

export function ScoreSegmentDialog({
  app: {
    profile: { tenant, role, siteRole },
  },
  isOpen,
  actions,
  enqueueNotification,
  selectedSegment,
}) {
  const platformConfig = usePlatformConfig();

  const getQuery = useCallback(() => {
    const query = {
      filters: [
        {
          name: 'recent_status',
          op: 'in',
          val: [
            CONSTANTS.TAL_MODEL.STATE.LIFT_CHART_GENERATION_SUCCESS,
            CONSTANTS.TAL_MODEL.STATE.MODEL_REMEDIATION_SETUP_SUCCESS,
            CONSTANTS.TAL_MODEL.STATE.MODEL_REMEDIATION_PENDING,
            CONSTANTS.TAL_MODEL.STATE.MODEL_REMEDIATION_SUCCESS,
          ],
        },
        { name: 'data_model_type', op: 'neq', val: CONSTANTS.TAL_MODEL.MODEL_CLASS.ACTUARIAL },
      ],
      order_by: [
        {
          field: 'id',
          direction: 'desc',
        },
      ],
      fields:
        'items{id,name,hiddenByRole,dataModelType,framework,modelData{dataPayload{actuarial_segment_id,positive_segment_id,negative_segment_id,validation_segment_id},metadataPayload},predictionType,typeDetail},*',
    };
    return query;
  }, []);
  const {
    data: modelList = [],
    error,
    isLoading,
    mutate: getData,
  } = useModels({
    query: getQuery(),
    config: {
      includeRefresh: platformConfig.includeRefresh,
    },
  });

  const filteredModels = useMemo(
    () =>
      modelList.filter(
        (mdl) => !checkHiddenByRole(role, mdl.hiddenByRole) || !checkHiddenByRole(siteRole, mdl.hiddenByRole)
      ),
    [modelList, role, siteRole]
  );

  const apiStatus = useMemo(() => {
    if (error) {
      return CONSTANTS.API.FAILED;
    }
    if (isLoading) {
      return CONSTANTS.API.STARTED;
    }
    return CONSTANTS.API.SUCCEEDED;
  }, [error, isLoading]);

  const [model, setModel] = useState(null);
  const [compState, setCompState] = useState(CONSTANTS.COMPONENT.VALIDATION.PENDING);

  const { handleSubmit, errors, register, setValue, reset } = useForm({ defaultValues });
  const onSubmit = (data) => {
    actions.handlers[0].on(data);
    actions.close.on();
  };
  const formSubmit = handleSubmit(onSubmit);

  const resetForm = useCallback(() => {
    reset(defaultValues);
    setModel(null);
  }, [reset]);

  useEffect(() => {
    register(
      { name: 'model', type: 'custom' },
      {
        required: 'Model is required',
        validate: (m) => {
          if (platformConfig.includeRefresh) {
            if (m?.modelData?.metadataPayload?.refresh?.platform < selectedSegment?.refresh?.platform) {
              return "Selected model's platform uses the legacy platform. Please select a model that uses the upgraded platform.";
            }
            if (m?.modelData?.metadataPayload?.refresh?.platform > selectedSegment?.refresh?.platform) {
              return "Selected model's platform uses the upgraded platform. Please select a model that uses the legacy platform.";
            }
            return (
              m?.modelData?.metadataPayload?.refresh?.platform === selectedSegment?.refresh?.platform ||
              'Selected model does not use a valid platform for selected segment'
            );
          }
          return true;
        },
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSegment]);

  useEffect(() => {
    if (tenant) getData();
  }, [tenant, getData]);

  useEffect(() => {
    if (error) setCompState(CONSTANTS.COMPONENT.VALIDATION.FAILED);
    else if (!isLoading && !error) {
      const hasModels = filteredModels.length > 0;
      if (hasModels) {
        setCompState(CONSTANTS.COMPONENT.VALIDATION.VALIDATED);
      } else {
        setCompState(CONSTANTS.COMPONENT.VALIDATION.INSUFFICIENT_DATA);
      }
    }
  }, [filteredModels, error, isLoading]);

  useEffect(() => {
    if (compState === CONSTANTS.COMPONENT.VALIDATION.FAILED) {
      enqueueNotification('Unable to fetch models. Please try again later');
    }
  }, [compState, enqueueNotification]);

  useLayoutEffect(() => {
    ReactTooltip.rebuild();
  });

  return (
    <BaseDialog
      isOpen={isOpen}
      hideCloseIcon
      actions={{
        ...actions,
        cancel: {
          ...actions.cancel,
          before: resetForm,
        },
        close: {
          ...actions.close,
          before: resetForm,
        },
        handlers: [
          {
            on: compState === CONSTANTS.COMPONENT.VALIDATION.VALIDATED ? formSubmit : actions.close.on,
          },
        ],
      }}
      items={{
        buttons: [
          {
            text: compState === CONSTANTS.COMPONENT.VALIDATION.VALIDATED ? 'PREDICT' : 'OKAY',
            textSize: 'body',
            close: false,
          },
        ],
        content: [
          <Switch key="items-0" status={compState}>
            <Div value={CONSTANTS.COMPONENT.VALIDATION.PENDING} d="flex" align="center">
              <Div textSize="subheader" m={{ y: '1rem' }} textColor="tabSelected">
                Loading... Please wait!
              </Div>
            </Div>
            <Div value={CONSTANTS.COMPONENT.VALIDATION.INSUFFICIENT_DATA} d="flex" align="center">
              <Div textSize="subheader" m={{ y: '1rem' }} textColor="tabSelected">
                No models have yet been generated. Please first create a new model; once completed, it can be used to
                make go-forward predictions on Target datasets.
              </Div>
            </Div>
            <Div value={CONSTANTS.COMPONENT.VALIDATION.FAILED} d="flex" align="center">
              <Image w="100%" type="error" src="/static/warning.svg" />
            </Div>
            <Div value={CONSTANTS.COMPONENT.VALIDATION.VALIDATED} d="flex" flexDir="column">
              <Text textSize="input" textWeight="500" textColor="shade66">
                Select the model you want to use to write predictions
              </Text>
              <ModelsDropdown
                dropDownStyle={{
                  'data-test': 'model-dropdown',
                  w: '100%',
                  h: '1.875rem',
                  m: { y: '.375rem' },
                  DropdownButtonOptions: {
                    h: '1.875rem',
                    w: '1.875rem',
                    rounded: { tr: 'md', br: 'md' },
                    border: { y: '1px solid', r: '1px solid', l: '0' },
                    borderColor: 'shade79',
                    align: 'center',
                    justify: 'center',
                    d: 'flex',
                    p: '0',
                    'data-test': 'model-dropdown-btn',
                  },
                  inputStyle: {
                    h: 'calc(1.875rem-1px)',
                    textColor: 'shade99',
                    flexGrow: '1',
                    p: { l: '0' },
                    rounded: 'none',
                    'data-test': 'model-dropdown-input',
                  },
                  menuOptions: {
                    pos: 'absolute',
                    shadow: 4,
                    minW: '100%',
                    rounded: 'md',
                    maxH: '250px',
                    bg: 'white',
                    overflow: 'auto',
                  },
                  menuItemOptions: {
                    d: 'flex',
                    align: 'center',
                    p: { x: '1rem', y: '0.5rem' },
                    hoverBg: 'gray100',
                    bg: 'white',
                    'data-test': 'model-dropdown-menu-item',
                  },
                  DivOptions: {
                    rounded: { l: 'md', r: '0' },
                    border: '1px solid',
                    borderColor: 'shade79',
                    p: { l: '0.75rem' },
                  },
                  openSuffix: <Icon color="shade66" name="UpArrow" size=".625rem" />,
                  closeSuffix: <Icon color="shade66" name="DownArrow" size=".625rem" />,
                }}
                loading={apiStatus === CONSTANTS.API.STARTED}
                models={platformConfig.getModels(filteredModels)}
                model={model}
                defaultText="Please select a model"
                onSelectModel={(nextModel) => {
                  setModel(nextModel);
                  setValue('model', nextModel);
                }}
              />
              <ErrorMessage
                errors={errors}
                name="model"
                render={({ message }) => {
                  if (![undefined, null, ''].includes(message)) {
                    return (
                      <Text textColor="danger800" textSize="caption" m={{ t: '.125rem' }}>
                        {message}
                      </Text>
                    );
                  }
                  return null;
                }}
              />
            </Div>
          </Switch>,
          <Div key="items-spacer" data-test="last-spacer" h="1.875rem" m={{ t: '1.25rem' }} />,
        ],
      }}
      title={{
        text: 'Make Batch Predictions',
        backgroundColor: 'talLime',
        color: 'white',
        textSize: 'title',
        icon: <Icon name="ThinkalikeModel" size="2.375rem" color="white" alignSelf="center" justifySelf="center" />,
      }}
    />
  );
}

const mapStateToProps = ({ app }, ownProps) => ({
  app,
  ...ownProps,
});

const mapDispatchToProps = (dispatch) => ({
  fetchModels: (...params) => dispatch(segmentsListActions.fetchModels(...params)),
  enqueueNotification: (...params) => dispatch(appActions.enqueueNotification(...params)),
});

ScoreSegmentDialog.propTypes = {
  app: PropTypes.shape({
    client: PropTypes.shape({}),
    profile: PropTypes.shape({
      tenant: PropTypes.string.isRequired,
      role: PropTypes.string.isRequired,
      siteRole: PropTypes.string.isRequired,
    }),
  }).isRequired,
  selectedSegment: PropTypes.shape({
    refresh: PropTypes.shape({
      platform: PropTypes.number.isRequired,
    }).isRequired,
  }).isRequired,
  isOpen: PropTypes.bool.isRequired,
  actions: PropTypes.shape({
    cancel: PropTypes.shape({
      before: PropTypes.func,
      on: PropTypes.func.isRequired,
      after: PropTypes.func,
    }).isRequired,
    close: PropTypes.shape({
      before: PropTypes.func,
      on: PropTypes.func.isRequired,
      after: PropTypes.func,
    }).isRequired,
    handlers: PropTypes.arrayOf(
      PropTypes.shape({
        before: PropTypes.func,
        on: PropTypes.func.isRequired,
        after: PropTypes.func,
      })
    ),
  }).isRequired,
  // eslint-disable-next-line react/require-default-props
  title: PropTypes.shape({
    text: PropTypes.string.isRequired,
    textSize: PropTypes.string,
    backgroundColor: PropTypes.string.isRequired,
    color: PropTypes.string,
  }),
  items: PropTypes.shape({
    content: PropTypes.arrayOf(PropTypes.element).isRequired,
    buttons: PropTypes.arrayOf(
      PropTypes.shape({
        text: PropTypes.string.isRequired,
        textSize: PropTypes.string,
        close: PropTypes.bool.isRequired,
      }).isRequired
    ),
  }).isRequired,
  enqueueNotification: PropTypes.func.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(ScoreSegmentDialog);
